import React from 'react'
import { useAppSelector } from '@SonarCX/hooks/redux'

import Notes from '../../components/organisms/Notes'
import PinnedTabs from '@SonarCX/components/organisms/PinnedTabs'

import { selectPinnedTabs } from '@SonarCX/slices/pinnedTabs'
import { isEmpty } from '@SonarCX/components/organisms/LexicalEditor'
import { useAreYouSure } from '../AreYouSureProvider'
import useKeyListener from '@SonarCX/hooks/useKeyListener'

type NoteVisibilityType = {
  currentNote: APIAgentNote
  setCurrentNote: (note: APIAgentNote) => void
  tabNotes: APIAgentNote[]
  modalWidth: number
  setModalWidth: (value: number) => void
  open: boolean
  setOpen: React.Dispatch<React.SetStateAction<boolean>>
  toggle: () => void
}

const NoteVisibilityContext = React.createContext<NoteVisibilityType>(null)

const NoteVisibilityProvider = ({ children }) => {
  const { tabs } = useAppSelector(selectPinnedTabs)
  const { setAction, setTitle, setV2, setSubtext } = useAreYouSure()

  const [currentNote, setCurrentNote] = React.useState<APIAgentNote>(null)
  const [modalWidth, setModalWidth] = React.useState(350)
  const [open, setOpen] = React.useState(false)
  const editing = Boolean(currentNote?.id)

  const notes = React.useMemo(
    () => tabs.filter(tab => tab.type === 'note') as unknown as APIAgentNote[],
    [tabs]
  )

  const useModalWidth = React.useMemo(
    () => (open ? modalWidth : null),
    [open, modalWidth]
  )

  useKeyListener({ key: 'Escape', callback: () => close() })

  const toggle = React.useCallback(() => {
    setOpen(old => !old)
  }, [])

  const close = React.useCallback(() => {
    const doClose = () => {
      // Close modal
      if (currentNote === null) setOpen(false)
      // Go back to inital screen
      else setCurrentNote(null)
    }
    if (!editing && currentNote && !isEmpty(currentNote?.content)) {
      setV2(true)
      setTitle('Are you sure?')
      setSubtext('Continuing will lose changes to this new note')
      setAction(doClose)
      return
    }
    doClose()
  }, [currentNote, editing, setAction, setSubtext, setTitle, setV2])

  return (
    <NoteVisibilityContext.Provider
      value={{
        currentNote,
        setCurrentNote,
        tabNotes: notes,
        modalWidth,
        setModalWidth,
        open,
        setOpen,
        toggle
      }}
    >
      {children}

      {open && <Notes onClose={close} />}

      <PinnedTabs useModalWidth={useModalWidth} />
    </NoteVisibilityContext.Provider>
  )
}

export default NoteVisibilityProvider

export const useNoteVisibility = () => React.useContext(NoteVisibilityContext)
